import { useState } from "preact/compat"
import { useAsyncEffect } from "@/utils"
import { listContracts, deleteContract, ContractDocument } from "@/api/contract"
import { Button } from "@/components/ui/button"
import { ContractUploadModal } from "./upload-modal"
import { LoadingSpinner } from "@/app/loading"
import { FileText, Plus, Trash2, ExternalLink, RefreshCw, AlertCircle, CheckCircle, Loader2 } from "lucide-react"
import { useLocation } from "wouter-preact"
import { format, parseISO } from "date-fns"

interface ContractsListProps {
  source: string
  integrationId?: string
}

export function ContractsList({ source, integrationId }: ContractsListProps) {
  const [, setLocation] = useLocation()
  const [contracts, setContracts] = useState<ContractDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [uploadModalOpen, setUploadModalOpen] = useState(false)

  async function loadContracts() {
    setLoading(true)
    try {
      const data = await listContracts(source, integrationId)
      setContracts(data)
    } catch (e) {
      console.error("Failed to load contracts:", e)
    } finally {
      setLoading(false)
    }
  }

  useAsyncEffect(loadContracts, [source, integrationId])

  async function handleDelete(id: string) {
    if (!confirm("Are you sure you want to delete this contract?")) {
      return
    }
    try {
      await deleteContract(id)
      setContracts(contracts.filter(c => c.id !== id))
    } catch (e) {
      console.error("Failed to delete contract:", e)
    }
  }

  function handleUploadSuccess(contract: ContractDocument) {
    setContracts([contract, ...contracts])
    setLocation(`/settings/contracts/${contract.id}`)
  }

  function getStatusIcon(status: string) {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case "analyzing":
        // Lucide only merges `className`; spin on a wrapper so it actually animates.
        return (
          <span class="inline-flex shrink-0 motion-safe:animate-spin" aria-hidden>
            <Loader2 className="h-4 w-4 text-blue-500" />
          </span>
        )
      case "failed":
        return <AlertCircle className="h-4 w-4 text-red-500" />
      default:
        return <RefreshCw className="h-4 w-4 text-gray-400" />
    }
  }

  function getKindLabel(kind: string) {
    switch (kind) {
      case "marketing":
        return "Marketing"
      case "freight":
        return "Freight"
      case "purchasing":
        return "Purchasing"
      default:
        return kind
    }
  }

  if (loading) {
    return (
      <div class="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div class="space-y-4">
      <div class="flex items-center justify-between">
        <p class="text-sm text-gray-500">
          Upload contracts to provide context for deduction analysis.
        </p>
        <Button onClick={() => setUploadModalOpen(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Upload Contract
        </Button>
      </div>

      {contracts.length === 0 ? (
        <div class="text-center py-8 border-2 border-dashed rounded-lg">
          <FileText className="h-12 w-12 mx-auto text-gray-300 mb-4" />
          <p class="text-gray-500 mb-4">No contracts uploaded yet</p>
          <Button variant="outline" onClick={() => setUploadModalOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Upload Your First Contract
          </Button>
        </div>
      ) : (
        <div class="space-y-2">
          {contracts.map(contract => (
            <div
              key={contract.id}
              class="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
            >
              <div
                class="flex items-center gap-3 flex-1 cursor-pointer"
                onClick={() => setLocation(`/settings/contracts/${contract.id}`)}
              >
                <FileText className="h-5 w-5 text-gray-400" />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <span class="font-medium truncate">{contract.name}</span>
                    <span class="text-xs px-2 py-0.5 bg-gray-100 rounded-full">
                      {getKindLabel(contract.kind)}
                    </span>
                    {contract.version > 1 && (
                      <span class="text-xs text-gray-500">v{contract.version}</span>
                    )}
                  </div>
                  <div class="flex items-center gap-2 text-xs text-gray-500">
                    <span>Uploaded {format(parseISO(contract.created_at), "MMM d, yyyy")}</span>
                    <span class="flex items-center gap-1">
                      {getStatusIcon(contract.analysis_status)}
                      {contract.analysis_status === "completed" ? "Analyzed" : contract.analysis_status}
                    </span>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setLocation(`/settings/contracts/${contract.id}`)}
                >
                  <ExternalLink className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(contract.id)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ContractUploadModal
        open={uploadModalOpen}
        onOpenChange={setUploadModalOpen}
        source={source}
        integrationId={integrationId}
        onSuccess={handleUploadSuccess}
      />
    </div>
  )
}
